import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export interface PreviewFile {
  name: string;
  url: string;
  contentType: string;
}

/** The types the preview shows; anything else gets no eye button. */
export function canPreview(contentType: string | undefined): boolean {
  return /^(application\/pdf|image\/(png|jpeg|gif|webp)|text\/(plain|csv))$/.test(
    contentType ?? ""
  );
}

const TYPES_BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  txt: "text/plain",
  csv: "text/csv",
};

/** A previewable type from a file name, for files known only by name and link (documents). */
export function contentTypeFromName(name: string): string | undefined {
  return TYPES_BY_EXTENSION[name.split(".").pop()?.toLowerCase() ?? ""];
}

/**
 * One file, full size: an image as an image, a PDF in the browser's viewer, text in a frame that
 * runs nothing. Full screen on a phone.
 */
export function FilePreviewDialog({
  file,
  onClose,
}: {
  file: PreviewFile | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={file !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[85vh] max-w-4xl flex-col gap-0 overflow-hidden p-0 max-sm:h-dvh max-sm:max-w-none max-sm:rounded-none max-sm:border-0">
        <DialogHeader className="border-b px-4 py-3 pr-12">
          <DialogTitle className="truncate text-sm">{file?.name}</DialogTitle>
        </DialogHeader>
        {file &&
          (file.contentType.startsWith("image/") ? (
            <div className="flex min-h-0 flex-1 items-center justify-center bg-muted p-4">
              <img
                src={file.url}
                alt={file.name}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ) : (
            <iframe
              title={file.name}
              src={file.url}
              className="min-h-0 w-full flex-1 bg-white"
              // Every frame is sandboxed but a PDF's (the browser's PDF viewer does not run in a
              // sandbox). On our own hosts a PDF is a blob typed by the server or the file's bytes;
              // a storage link elsewhere runs in that origin, which can't reach the app.
              {...(file.contentType !== "application/pdf" && { sandbox: "" })}
            />
          ))}
      </DialogContent>
    </Dialog>
  );
}
